import type { Listing, ListingVariant } from '@ps/models/lib/listing';

/** Single SKU attribute name (e.g. "Color", "Size"). */
export type VariantAttrType = string;
/** Selected value within an attribute (e.g. "Red", "Large"). */
export type VariantValueType = string;
/** Map from attribute name → selected value. */
export type VariantSelection = Record<VariantAttrType, VariantValueType>;

export interface VariantItemInfo {
  value: VariantValueType;
  selected: boolean;
  quantity: number;
  matches: ListingVariant[];
}

export interface VariantCategory {
  attr: VariantAttrType;
  /** Human-readable category name (from `listing.skuAttributes`). */
  name: string;
  variants: ListingVariant[];
  items: VariantItemInfo[];
}

/** Default attribute key for legacy variants that only have a name. */
export const kDefaultVariantAttr = '__name__';
/** Sentinel for "no value selected". */
export const kVariantAttrEmptyValue = '';

export function attrValueOfVariant(
  variant: ListingVariant,
  attr: VariantAttrType,
): VariantValueType {
  if (attr === kDefaultVariantAttr) return variant.name;
  return variant.skuAttributes?.[attr] ?? kVariantAttrEmptyValue;
}

/**
 * Pure port of RN's `buildMultiVariantsCategories`. Walks the listing's
 * `skuAttributes` (or falls back to `__name__` for legacy single-attr
 * variants), groups variants by attribute, and computes per-value
 * availability (which variants match + total inventory).
 */
export function buildVariantCategories(
  listing: Pick<Listing, 'variants' | 'skuAttributes'>,
  selection: VariantSelection,
): VariantCategory[] {
  const categories: VariantCategory[] = [];
  if (!listing.variants) return categories;

  const variantIdsWithSkuAttribute = new Set<string>();

  const selectedAttrs = Object.keys(selection).filter(
    x => selection[x] != null && selection[x] !== kVariantAttrEmptyValue,
  );

  type Group = { attr: VariantAttrType; variants: ListingVariant[] };

  const processGroup = (group: Group): VariantCategory => {
    const { attr, variants } = group;
    const otherSelectedAttrs = selectedAttrs.filter(x => x !== attr);
    const name = listing.skuAttributes?.[attr] ?? '';

    const valueSet = new Set(
      variants
        .map(variant => attrValueOfVariant(variant, attr))
        .filter((v): v is string => Boolean(v)),
    );

    const items: VariantItemInfo[] = [];
    for (const value of valueSet) {
      const selected = selection[attr] === value;
      let matches: ListingVariant[] = variants.filter(
        variant => attrValueOfVariant(variant, attr) === value,
      );
      // Filter by sibling-attribute selections to disable
      // currently-unreachable combinations.
      if (otherSelectedAttrs.length > 0) {
        matches = matches.filter(variant =>
          otherSelectedAttrs.every(
            x => attrValueOfVariant(variant, x) === selection[x],
          ),
        );
      }
      items.push({
        value,
        selected,
        quantity: matches.reduce((acc, v) => acc + (v.quantity ?? 0), 0),
        matches,
      });
    }

    for (const variant of variants) {
      variantIdsWithSkuAttribute.add(variant.id);
    }

    return { attr, name, variants, items };
  };

  const groups = Object.keys(listing.skuAttributes ?? {})
    .map<Group>(attr => ({
      attr,
      variants:
        listing.variants?.filter(
          variant => !variant.hidden && attrValueOfVariant(variant, attr),
        ) ?? [],
    }))
    .filter(x => x.variants.length > 0);

  for (const group of groups) categories.push(processGroup(group));

  // Variants without sku-attribute mapping fall into a default category
  // keyed by `__name__`.
  const defaultVariants = listing.variants.filter(
    x => !x.hidden && !variantIdsWithSkuAttribute.has(x.id),
  );
  if (defaultVariants.length > 0) {
    categories.unshift(
      processGroup({ attr: kDefaultVariantAttr, variants: defaultVariants }),
    );
  }
  return categories;
}

/**
 * Resolve the selected variant id from a `VariantSelection`. Returns
 * undefined when the selection is partial or matches >1 variant.
 */
export function resolveSelectedVariantId(
  listing: Pick<Listing, 'variants' | 'skuAttributes'>,
  selection: VariantSelection,
): string | undefined {
  const attrs = Object.keys(listing.skuAttributes ?? {});
  if (attrs.length === 0) {
    // Legacy single-name variants — match by `__name__`.
    const target = selection[kDefaultVariantAttr];
    if (!target) return undefined;
    const found = listing.variants?.find(v => !v.hidden && v.name === target);
    return found?.id;
  }
  const matches = (listing.variants ?? []).filter(
    variant =>
      !variant.hidden &&
      attrs.every(
        attr => attrValueOfVariant(variant, attr) === selection[attr],
      ),
  );
  return matches.length === 1 ? matches[0].id : undefined;
}
